import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import CompanyAvatar from "../../components/CompanyAvatar";
import ConsentBadge from "../../components/ConsentBadge/ConsentBadge";
import DashboardLayout from "../../components/DashboardLayout/DashboardLayout";
import DealOutcomeSummary from "../../components/DealOutcome/DealOutcomeSummary";
import { EMPTY_VALUE, formatDate } from "../../i18n/format";
import { getApiErrorMessage } from "../../lib/apiError";
import { locationLabel, regionLabel } from "../../lib/regionOptions";
import type { SupplierConsentStatus } from "../../lib/supplierConsent";
import { trustBadgeLabel, trustBadgeVariant } from "../../lib/trustLabels";
import {
  getMatchRequestsOpportunitiesListQueryKey,
  useMatchRequestsOpportunitiesAcceptCreate,
  useMatchRequestsOpportunitiesDeclineCreate,
  useMatchRequestsOpportunitiesList,
} from "../../services/match-requests/generated/match-requests/match-requests";
import type { Opportunity } from "../../services/match-requests/generated/model";
import { useConversationsCreateCreate } from "../../services/messages/generated/conversations/conversations";
import "./OpportunitiesPage.css";

type Filter = "all" | SupplierConsentStatus;

const FILTERS = [
  { value: "all", labelKey: "common:labels.all" },
  { value: "pending", labelKey: "domain:consent.supplier.pending" },
  { value: "accepted", labelKey: "domain:consent.supplier.accepted" },
  { value: "declined", labelKey: "domain:consent.supplier.declined" },
] as const satisfies readonly { value: Filter; labelKey: string }[];

const PAGE_SIZE = 100;

function regionsLabel(raw: string | null | undefined): string | null {
  if (!raw) return null;
  return raw
    .split(",")
    .map((r) => r.trim())
    .filter(Boolean)
    .map((r) => regionLabel(r))
    .join(", ");
}

/**
 * WP1.6 — the matched supplier's own view of its deals. Accept opens chat
 * with the buyer; decline (optional reason, seen only by MatchPoint) closes it.
 */
const OpportunitiesPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(["b2b", "common", "domain"]);
  const location = useLocation();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>("all");
  const highlightId = (location.state as { openOpportunityId?: string } | null)?.openOpportunityId ?? null;

  const { data, isLoading, error } = useMatchRequestsOpportunitiesList({
    ...(filter === "all" ? {} : { consentStatus: filter }),
    page_size: PAGE_SIZE,
  });
  const opportunities: Opportunity[] = data?.results ?? [];

  useEffect(() => {
    if (!highlightId || isLoading) return;
    document.getElementById(`opportunity-${highlightId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightId, isLoading]);

  return (
    <DashboardLayout>
      <header className="opportunities__header">
        <h1 className="dashboard__title">{t("opportunities.title")}</h1>
        <p className="dashboard__subtitle">{t("opportunities.subtitle")}</p>
      </header>

      <div className="opportunities__filters" role="tablist" aria-label={t("opportunities.filterAria")}>
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            role="tab"
            aria-selected={filter === f.value}
            className={`opportunities__filter${filter === f.value ? " opportunities__filter--active" : ""}`}
            onClick={() => setFilter(f.value)}
          >
            {t(f.labelKey)}
          </button>
        ))}
      </div>

      {error ? (
        <div className="opportunities__error" role="alert">
          <i className="fa-solid fa-circle-exclamation" />
          <span>{t("opportunities.errors.load")}</span>
        </div>
      ) : isLoading ? (
        <div className="opportunities__loading">
          <i className="fa-solid fa-spinner fa-spin" />
          <span>{t("opportunities.loading")}</span>
        </div>
      ) : opportunities.length === 0 ? (
        <p className="opportunities__empty">
          {filter === "all" ? t("opportunities.empty.all") : t("opportunities.empty.filtered")}
        </p>
      ) : (
        <div className="opportunities__list">
          {opportunities.map((opp) => (
            <OpportunityCard
              key={opp.id}
              opportunity={opp}
              highlighted={String(opp.id) === highlightId}
              onChanged={() => queryClient.invalidateQueries({ queryKey: getMatchRequestsOpportunitiesListQueryKey() })}
              onOpenConversation={(conversationId) =>
                navigate("/b2b/messages", { state: { openConversationId: conversationId } })
              }
            />
          ))}
        </div>
      )}
    </DashboardLayout>
  );
};

interface OpportunityCardProps {
  opportunity: Opportunity;
  highlighted: boolean;
  onChanged: () => void;
  onOpenConversation: (conversationId: string) => void;
}

const OpportunityCard = ({ opportunity: opp, highlighted, onChanged, onOpenConversation }: OpportunityCardProps) => {
  const { t } = useTranslation(["b2b", "common"]);
  const { t: tDeals } = useTranslation("deals");
  const navigate = useNavigate();
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);

  const acceptMutation = useMatchRequestsOpportunitiesAcceptCreate({
    mutation: {
      onSuccess: () => {
        setActionError(null);
        onChanged();
      },
      onError: (err) => setActionError(getApiErrorMessage(err, t("opportunities.errors.accept"))),
    },
  });
  const declineMutation = useMatchRequestsOpportunitiesDeclineCreate({
    mutation: {
      onSuccess: () => {
        setActionError(null);
        setDeclining(false);
        onChanged();
      },
      onError: (err) => setActionError(getApiErrorMessage(err, t("opportunities.errors.decline"))),
    },
  });
  const conversationMutation = useConversationsCreateCreate({
    mutation: {
      onSuccess: (conv) => onOpenConversation(String(conv.id)),
      onError: (err) => setActionError(getApiErrorMessage(err, t("opportunities.errors.openConversation"))),
    },
  });

  const { request, buyer } = opp;
  const busy = acceptMutation.isPending || declineMutation.isPending;
  const details: [string, string | null | undefined][] = [
    [t("opportunities.details.productService"), request.productServiceName],
    [t("opportunities.details.hsCode"), request.hsCode],
    [t("opportunities.details.quantity"), request.quantityVolume],
    [t("opportunities.details.targetRegions"), regionsLabel(request.preferredRegions)],
    [t("opportunities.details.timeline"), request.timeline],
  ];
  const buyerMeta = [buyer.companyIndustry, locationLabel(buyer.country, buyer.region)].filter(Boolean).join(" · ");

  return (
    <article
      id={`opportunity-${opp.id}`}
      className={`opportunities__card${highlighted ? " opportunities__card--highlighted" : ""}`}
      aria-label={t("opportunities.cardAria", { title: request.requirementTitle })}
    >
      <header className="opportunities__card-header">
        <div>
          <p className="opportunities__eyebrow">{t("opportunities.matchedOn", { date: formatDate(opp.createdAt) })}</p>
          <h2 className="opportunities__title">{request.requirementTitle}</h2>
        </div>
        <ConsentBadge status={opp.consentStatus} audience="supplier" />
      </header>

      <section className="opportunities__buyer">
        <CompanyAvatar src={buyer.companyAvatar} name={buyer.companyName} size="md" />
        <div>
          <p className="opportunities__buyer-name">
            {buyer.companyName}
            <span className={`opportunities__trust opportunities__trust--${trustBadgeVariant(buyer)}`}>
              {trustBadgeLabel(buyer)}
            </span>
          </p>
          {buyerMeta && <p className="opportunities__muted">{buyerMeta}</p>}
          {buyer.companyDescription && <p className="opportunities__muted">{buyer.companyDescription}</p>}
          {buyer.referralDisclosure ? (
            <p className="opportunities__muted">
              <em>{t("opportunities.disclosure", { text: buyer.referralDisclosure })}</em>
            </p>
          ) : null}
        </div>
      </section>

      <dl className="opportunities__details">
        {details
          .filter(([, value]) => Boolean(value))
          .map(([label, value]) => (
            <div key={label} className="opportunities__detail">
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        <div className="opportunities__detail">
          <dt>{t(`opportunities.yourMatched.${opp.matchedItem?.type ?? "item"}`)}</dt>
          <dd>{opp.matchedItem?.name ?? EMPTY_VALUE}</dd>
        </div>
        <div className="opportunities__detail">
          <dt>{t("opportunities.winWinScore")}</dt>
          <dd>{opp.winWinMatchScore}%</dd>
        </div>
      </dl>

      {opp.consentStatus === "accepted" && <DealOutcomeSummary outcome={opp.outcome} />}

      {actionError && (
        <p className="opportunities__inline-error" role="alert">
          {actionError}
        </p>
      )}

      {opp.consentStatus === "pending" && !declining && (
        <footer className="opportunities__actions">
          <button
            type="button"
            className="opportunities__button opportunities__button--secondary"
            disabled={busy}
            onClick={() => setDeclining(true)}
          >
            {t("opportunities.decline")}
          </button>
          <button
            type="button"
            className="opportunities__button opportunities__button--primary"
            disabled={busy}
            onClick={() => acceptMutation.mutate({ id: opp.id })}
          >
            <i className="fa-solid fa-check" />
            {acceptMutation.isPending ? t("opportunities.accepting") : t("opportunities.accept")}
          </button>
        </footer>
      )}

      {opp.consentStatus === "pending" && declining && (
        <div className="opportunities__decline">
          <label htmlFor={`decline-reason-${opp.id}`}>{t("opportunities.reasonLabel")}</label>
          <textarea
            id={`decline-reason-${opp.id}`}
            maxLength={1000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("opportunities.reasonPlaceholder")}
          />
          <p className="opportunities__muted">{t("opportunities.reasonHint")}</p>
          <div className="opportunities__actions">
            <button
              type="button"
              className="opportunities__button opportunities__button--secondary"
              disabled={busy}
              onClick={() => setDeclining(false)}
            >
              {t("common:actions.cancel")}
            </button>
            <button
              type="button"
              className="opportunities__button opportunities__button--danger"
              disabled={busy}
              onClick={() => declineMutation.mutate({ id: opp.id, data: { reason: reason.trim() } })}
            >
              {declineMutation.isPending ? t("opportunities.declining") : t("opportunities.declineMatch")}
            </button>
          </div>
        </div>
      )}

      {opp.consentStatus === "accepted" && (
        <footer className="opportunities__actions">
          <span className="opportunities__muted">
            {t("opportunities.acceptedOn", { date: formatDate(opp.consentRespondedAt) })}
          </span>
          <button type="button" className="opportunities__button" onClick={() => navigate(`/b2b/deals/${opp.id}`)}>
            <i className="fa-regular fa-folder-open" />
            {tDeals("workspace.openDeal")}
          </button>
          <button
            type="button"
            className="opportunities__button opportunities__button--primary"
            disabled={conversationMutation.isPending}
            onClick={() =>
              opp.conversationId
                ? onOpenConversation(opp.conversationId)
                : conversationMutation.mutate({ data: { matchResultId: opp.id } })
            }
          >
            <i className="fa-regular fa-comment-dots" />
            {opp.conversationId ? t("opportunities.openConversation") : t("opportunities.messageBuyer")}
          </button>
        </footer>
      )}

      {opp.consentStatus === "declined" && (
        <footer className="opportunities__actions">
          <span className="opportunities__muted">
            {opp.declineReason
              ? t("opportunities.declinedOnWithReason", {
                  date: formatDate(opp.consentRespondedAt),
                  reason: opp.declineReason,
                })
              : t("opportunities.declinedOn", { date: formatDate(opp.consentRespondedAt) })}
          </span>
        </footer>
      )}
    </article>
  );
};

export default OpportunitiesPage;
